import { useState, useEffect, useCallback } from "react";
import {
  Home,
  Building,
  FileText,
  DollarSign,
  MessageSquare,
  UserCheck,
  Shield,
  Eye,
  MapPin,
  Phone,
  Mail,
  Users,
  Calendar,
} from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { usePrefixedNavigate } from "../../hooks/usePrefixedNavigate";

interface Hostel {
  id: string;
  name: string;
  type: string;
  hostel_type: string | null;
  city: string | null;
  state: string | null;
  total_capacity: number;
  occupied_count: number | null;
  number_of_rooms: number | null;
  email: string | null;
  phone: string | null;
  status: string | null;
  funding_needed: number | null;
  monthly_operational_cost: number | null;
  facilities: string[] | null;
  year_established: number | null;
  address_line1: string | null;
}

export default function Hostels() {
  const { user } = useAuth();
  const navigate = usePrefixedNavigate();
  const [hostel, setHostel] = useState<Hostel | null>(null);
  const [loading, setLoading] = useState(true);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  const sidebarItems = [
    {
      label: "Dashboard",
      path: "/jainboarding-admin/dashboard/hostel-admin",
      icon: <Home className="h-5 w-5" />,
    },
    {
      label: "Hostels",
      path: "/jainboarding-admin/hostels",
      icon: <Building className="h-5 w-5" />,
    },
    {
      label: "Scholarships",
      path: "/jainboarding-admin/scholarships",
      icon: <FileText className="h-5 w-5" />,
    },
    {
      label: "Donation Status",
      path: "/jainboarding-admin/funding",
      icon: <DollarSign className="h-5 w-5" />,
    },
    {
      label: "Reports",
      path: "/jainboarding-admin/reports",
      icon: <FileText className="h-5 w-5" />,
    },
    {
      label: "CMS",
      path: "/jainboarding-admin/cms",
      icon: <MessageSquare className="h-5 w-5" />,
    },
    {
      label: "CRM",
      path: "/jainboarding-admin/crm",
      icon: <UserCheck className="h-5 w-5" />,
    },
    {
      label: "Settings",
      path: "/jainboarding-admin/settings",
      icon: <Shield className="h-5 w-5" />,
    },
  ];

  const fetchHostel = useCallback(async () => {
    if (!user) {
      return;
    }

    try {
      const { data, error } = await supabase
        .from("hostels")
        .select("*")
        .eq("admin_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") {
        throw error;
      }
      setHostel(data);
    } catch (error) {
      console.error("Error fetching hostel:", error);
      toast.error("Failed to fetch hostel details");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchHostel();
  }, [user, fetchHostel]);

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-green-100 text-green-800">Approved</Badge>;
      case "pending":
        return (
          <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
            Pending
          </Badge>
        );
      case "rejected":
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <DashboardLayout
        sidebarItems={sidebarItems}
        currentPath="/jainboarding-admin/hostels"
        userName={user?.email ?? user?.phone ?? "Admin"}
        userPhone=""
      >
        <div className="flex min-h-[60vh] items-center justify-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!hostel) {
    return (
      <DashboardLayout
        sidebarItems={sidebarItems}
        currentPath="/jainboarding-admin/hostels"
        userName={user?.email ?? user?.phone ?? "Admin"}
        userPhone=""
      >
        <div className="flex min-h-[60vh] items-center justify-center">
          <Card className="w-full max-w-md text-center">
            <CardHeader>
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                <Building className="h-8 w-8 text-primary" />
              </div>
              <CardTitle>No Hostel Found</CardTitle>
              <CardDescription>
                You haven&apos;t registered a hostel yet.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => navigate("/hostel/onboarding")}
                className="w-full"
                size="lg"
              >
                Register Your Hostel
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      sidebarItems={sidebarItems}
      currentPath="/jainboarding-admin/hostels"
      userName={user?.email ?? user?.phone ?? "-"}
      userPhone=""
    >
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="mb-2 text-3xl font-bold text-foreground">
              My Hostel
            </h2>
            <p className="text-muted-foreground">
              View and manage your hostel details
            </p>
          </div>
          {getStatusBadge(hostel.status)}
        </div>

        {/* Pending Status Alert */}
        {hostel.status === "pending" && (
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100">
                  <Calendar className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="font-semibold text-yellow-800">
                    Application Under Review
                  </p>
                  <p className="text-sm text-yellow-600">
                    Your hostel registration is pending approval from the admin.
                    You&apos;ll be notified once it&apos;s approved.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rejected Status Alert */}
        {hostel.status === "rejected" && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                  <Calendar className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="font-semibold text-red-800">
                    Application Rejected
                  </p>
                  <p className="text-sm text-red-600">
                    Your hostel registration was rejected. Please contact
                    support for more information.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Hostel Overview Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl">{hostel.name}</CardTitle>
                <CardDescription className="mt-1 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  {hostel.city}, {hostel.state}
                </CardDescription>
              </div>
              <Button
                variant="outline"
                onClick={() => setIsViewDialogOpen(true)}
              >
                <Eye className="mr-2 h-4 w-4" />
                View All Details
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Capacity</p>
                  <p className="text-xl font-semibold">
                    {hostel.total_capacity}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                  <Building className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Rooms</p>
                  <p className="text-xl font-semibold">
                    {hostel.number_of_rooms ?? "N/A"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
                  <UserCheck className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Occupied</p>
                  <p className="text-xl font-semibold">
                    {hostel.occupied_count ?? 0}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100">
                  <Calendar className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Established</p>
                  <p className="text-xl font-semibold">
                    {hostel.year_established ?? "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact & Financial Info */}
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {hostel.email && (
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>{hostel.email}</span>
                </div>
              )}
              {hostel.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{hostel.phone}</span>
                </div>
              )}
              {hostel.address_line1 && (
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{hostel.address_line1}</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Financial Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Funding Needed</span>
                <span className="font-semibold">
                  ₹{(hostel.funding_needed ?? 0).toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">
                  Monthly Operational Cost
                </span>
                <span className="font-semibold">
                  ₹{(hostel.monthly_operational_cost ?? 0).toLocaleString()}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Facilities */}
        {hostel.facilities && hostel.facilities.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Facilities</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {hostel.facilities.map(facility => (
                  <Badge key={facility} variant="outline" className="px-3 py-1">
                    {facility}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* View Details Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{hostel.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <p className="font-medium">{hostel.type}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Hostel Type</p>
                  <p className="font-medium">{hostel.hostel_type ?? "N/A"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">City</p>
                  <p className="font-medium">{hostel.city}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">State</p>
                  <p className="font-medium">{hostel.state}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Total Capacity
                  </p>
                  <p className="font-medium">{hostel.total_capacity}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Number of Rooms
                  </p>
                  <p className="font-medium">
                    {hostel.number_of_rooms ?? "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Occupied</p>
                  <p className="font-medium">{hostel.occupied_count ?? 0}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  {getStatusBadge(hostel.status)}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{hostel.email ?? "N/A"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{hostel.phone ?? "N/A"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Funding Needed
                  </p>
                  <p className="font-medium">
                    ₹{(hostel.funding_needed ?? 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Monthly Cost</p>
                  <p className="font-medium">
                    ₹{(hostel.monthly_operational_cost ?? 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
